import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fdate } from "@/api";
import { PageHeader, EmptyState, RoleBadge } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, UserCog, KeyRound } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";
const EMPTY = { name: "", email: "", role: "accounts", password: "" };

const errText = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addRole = async (e) => {
    e.preventDefault();
    if (!newRole.trim()) return;
    try {
      const r = await api.post("/roles", { name: newRole });
      toast.success(`Role "${r.data.name}" created — assign it to any user`);
      setNewRole("");
      load();
    } catch (err) {
      toast.error(errText(err, "Role create failed"));
    }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u.user_id);
    setForm({ name: u.name, email: u.email, role: u.role, password: "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing}`, payload);
        toast.success(`User updated${form.password ? " — password reset" : ""}`);
      } else {
        await api.post("/users", { ...form, password: form.password || undefined });
        toast.success(`${form.name} created — sign in with ${form.password ? "email + password" : "Google or email OTP"}`);
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (err) {
      toast.error(errText(err, "Save failed"));
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name} (${u.email})? Their sessions end immediately.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(errText(err, "Delete failed"));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Team & Users" sub="Create users with passwords, edit profiles, reset passwords or remove access">
        <Button onClick={openNew} data-testid="add-user-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> Create User
        </Button>
      </PageHeader>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="roles-card">
        <p className="text-xs font-mono2 uppercase tracking-[0.18em] text-emerald-600 mb-3">User Types (Roles)</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {roles.map((r) => (
            <span key={r.name} className={`px-3 py-1 rounded-full border text-xs font-mono2 uppercase tracking-wider ${r.fixed ? "border-slate-600 text-slate-700" : "border-emerald-500/40 text-emerald-600 bg-emerald-500/10"}`} data-testid={`role-chip-${r.name}`}>
              {r.name}{r.fixed ? "" : " · custom"}
            </span>
          ))}
        </div>
        <form onSubmit={addRole} className="flex gap-2">
          <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="New user type, e.g. sales_manager" data-testid="new-role-input"
            className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 max-w-xs" />
          <Button type="submit" data-testid="create-role-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
            <Plus className="w-4 h-4 mr-1" /> Create User Type
          </Button>
        </form>
        <p className="text-[11px] text-slate-500 mt-3">Custom roles get staff-level access (all operational tabs). Only the built-in admin role sees Team & Users, Custom Fields and Settings.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[["admin", "Full control — users, custom fields, settings, deletes"],
          ["accounts", "Invoices, purchases, ledgers, banks, income & expense"],
          ["backoffice", "Vendors, clients, products, stock & daily entries"]].map(([r, d]) => (
          <div key={r} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`role-info-${r}`}>
            <RoleBadge role={r} />
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>

      {users.length === 0 ? (
        <EmptyState icon={UserCog} title="No users yet" sub="Create your first team member" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Role</th><th className="px-5 py-3">Password Login</th>
                <th className="px-5 py-3">Joined</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">
                    {u.name} {u.user_id === me?.user_id && <span className="text-[10px] text-emerald-600 font-mono2">(you)</span>}
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{u.email}</td>
                  <td className="px-5 py-3.5"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-3.5">
                    {u.has_password ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><KeyRound className="w-3.5 h-3.5" /> Enabled</span>
                    ) : (
                      <span className="text-xs text-slate-500">OTP / Google only</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-slate-500">{fdate(u.created_at)}</td>
                  <td className="px-5 py-3.5 text-right space-x-1">
                    <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.user_id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-150">
                      Edit
                    </button>
                    {u.user_id !== me?.user_id && (
                      <button onClick={() => remove(u)} data-testid={`user-delete-${u.user_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit User Profile" : "Create User"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Full Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Email * (used at login)</Label>
              <Input type="email" required value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 disabled:opacity-50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Role *</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select" className={selCls}>
                {roles.map((r) => <option key={r.name} value={r.name} className="capitalize">{r.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{editing ? "Reset Password (leave blank to keep current)" : "Password (optional — enables password login)"}</Label>
              <Input type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "••••••••" : "Min 6 characters"} data-testid="user-password-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <Button type="submit" disabled={busy} data-testid="user-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : editing ? "Save Profile" : "Create User"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
